import type { ReactNode } from 'react';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Icon } from '@/components/entities/Sprite';
import { Button } from '@/components/ui/Button/button';
import { DialogClose, DialogDescription, DialogTitle } from '@/components/ui/Dialog/dialog';
import { cn } from '@/utils/cn';

interface ModalHeaderProps {
  headerTitle?: string;
  accessibleTitle?: string;
  headerClassName?: string;
  headerBackAction?: () => void;
  onCloseAction: () => void;
  hideCloseIcon?: boolean;
  headerChild?: ReactNode;
}

const ModalHeader = ({
  headerTitle,
  accessibleTitle,
  headerClassName,
  headerBackAction,
  onCloseAction,
  hideCloseIcon,
  headerChild,
}: ModalHeaderProps) => {
  const visibleTitle = headerTitle?.trim() || '';
  const dialogTitle = accessibleTitle?.trim() || visibleTitle || 'Modal';

  return (
    <div className={cn('z-10 flex items-center justify-between gap-2.5 relative py-4 px-4 lg:px-6 lg:py-6 max-w-106 mx-auto w-full', headerClassName)}>
      <div className="flex items-center gap-4">
        {headerBackAction && (
          <Button
            variant="secondary"
            onClick={headerBackAction}
            className="w-8 h-8 rounded-[3.125rem] p-1"
            aria-label="Go back"
          >
            <Icon name="arrow-left" className="text-white" />
          </Button>
        )}

        <DialogTitle asChild>
          <h2 className={cn('paragraph-s-medium font-normal text-c300', !visibleTitle && 'sr-only')}>
            {visibleTitle || dialogTitle}
          </h2>
        </DialogTitle>
        <VisuallyHidden.Root>
          <DialogDescription>
            {dialogTitle}
          </DialogDescription>
        </VisuallyHidden.Root>
      </div>
      {headerChild}
      {!hideCloseIcon && (
        <DialogClose asChild>
          <button
            type="button"
            className="size-8 rounded-full bg-black/30 flex items-center justify-center group"
            aria-label="Close"
            onClick={onCloseAction}
          >
            <Icon name="x_close" className="text-c200 group-hover:text-white transition-colors duration-300 size-4" />
          </button>
        </DialogClose>
      )}
    </div>
  );
};

ModalHeader.displayName = 'ModalHeader';

export default ModalHeader;
